import React, { useEffect } from 'react';
import * as TabilityTypes from 'types';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import theme from 'theme';
import { Helmet } from 'react-helmet';
import * as amplitudeUtils from 'utils/amplitude';
import { useHistory } from 'react-router-dom';

// Actions
import { sessionActions } from 'state/actions/';

// Images + components
import KoalaTextButton from 'koala/components/TextButton';
import KoalaButton from 'koala/components/Button';
import { useTranslation } from 'react-i18next';

import AICreate from './ai_create.png';
import MagicImport from './magic_import.png';
import PreviewTemplate from './PreviewTemplate';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  height: 100%;
`;
const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  margin-top: ${theme.spacing.x8};

  width: 100%;
  gap: ${theme.spacing.x8};

  a {
    color: ${theme.colors.blue};
  }
  h1 {
    font-size: 2.8rem;
    font-weight: 500;
    margin-bottom: ${theme.spacing.x4};
  }

  h3 {
    margin: ${theme.spacing.x2} 0;
  }

  @media ${theme.devices.laptop} {
    display: flex;
    width: 100%;
    gap: ${theme.spacing.x1};
    justify-content: center;
    align-items: center;
    flex-direction: column;
    min-width: initial;
    margin-top: ${theme.spacing.x4};
    padding: ${theme.spacing.x4};

    .hide-mobile {
      display: none;
    }

    .special-mobile {
      position: relative;
      bottom: ${theme.spacing.x3};
    }
  }
`;

const Content = styled.div`
  width: 80rem;
  margin: 0 auto;
  @media ${theme.devices.laptop} {
    margin-top: 0;
  }

  textarea {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif,
      'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol' !important;
    font-size: 1.4rem;
    line-height: 1.4;
    padding: ${theme.spacing.x1};
    margin: ${theme.spacing.x1} 0;
    min-height: 10rem;
    width: 50rem;
    border-radius: 4px;
    border: 1px solid #949494;
    &:placeholder {
      color: #949494;
      font-size: 1.4rem;
    }
  }
`;

const Footer = styled.div`
  margin-bottom: ${theme.spacing.x3};

  button.secondary {
    color: ${theme.colors.subtleText};
    text-decoration: none;
    font-weight: normal;
  }
`;

const ExperienceOptionsContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.x3};
  width: 100%;
  flex-wrap: wrap;
`;

const ExperienceOption = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: ${theme.spacing.x3} ${theme.spacing.x4};
  gap: ${theme.spacing.x2};
  align-items: center;

  border: solid 1px ${theme.colors.N20};
  border-radius: 8px;

  img {
    height: 173px;
  }

  :hover {
    background: ${theme.colors.B5};
    cursor: pointer;
  }
  :active {
    background: ${theme.colors.N10};
  }
`;

const WorkspaceLink = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.x2};
`;

interface Props {
  workspace: TabilityTypes.Workspace;
}

function WorkspaceOnboardingMethod(props: Props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { workspace } = props;
  const history = useHistory();

  useEffect(() => {
    amplitudeUtils.sendAmplitudeData('app.onboarding.done', {
      'Tability Platform': 'web',
      'Tability Version': 2,
    });
  }, []);

  const handleLogout = (e: any) => {
    dispatch(sessionActions.logout());
  };

  const handleClick = () => {
    history.push(`/${workspace.slug}/onboarding/trial`);
  };

  return (
    <Wrapper>
      <Helmet>
        <title>
          {workspace.name} {t('workspaceOnboardingDone.helmetTitle')}
        </title>
      </Helmet>
      <Container>
        <Content>
          <h1>How do you want to add goals to your workspace?</h1>
          <ExperienceOptionsContainer>
            <PreviewTemplate workspace={workspace} templateCallback={handleClick} />
            <ExperienceOption onClick={handleClick}>
              <h3>Create new goals with AI</h3>
              <img src={AICreate} alt={'Create goals with AI'} />
              <p>No goals? No problem. Create and refine your perfect plan in seconds.</p>
              <KoalaButton onClick={handleClick}>Generate goals with AI</KoalaButton>
            </ExperienceOption>
            <ExperienceOption onClick={handleClick}>
              <h3>Import existing goals</h3>
              <img src={MagicImport} alt={'Import your data'} />
              <p>Import your plan instantly from a spreadsheet or doc.</p>
              <KoalaButton onClick={handleClick}>Bring your data</KoalaButton>
            </ExperienceOption>
          </ExperienceOptionsContainer>
          <WorkspaceLink>
            <KoalaTextButton isLink to={`/${workspace.slug}`} appearance="subtle">
              Take me to the workspace
            </KoalaTextButton>
          </WorkspaceLink>
        </Content>
      </Container>
      <Footer>
        <KoalaTextButton onClick={handleLogout} appearance="subtle">
          {t('shared.logout')}
        </KoalaTextButton>
      </Footer>
    </Wrapper>
  );
}

export default WorkspaceOnboardingMethod;
